import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';

export default function DirectoryCategories() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [formData, setFormData] = useState({
    name_fr: '',
    name_en: ''
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchCategories();
    }
  }, [user]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/directory/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Erreur lors du chargement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editCategory) {
        // Update
        await axios.put(
          `${API}/directory/admin/categories/${editCategory.id}?admin_id=${user.id}&name_fr=${encodeURIComponent(formData.name_fr)}&name_en=${encodeURIComponent(formData.name_en)}`
        );
        toast.success('Catégorie mise à jour');
      } else {
        // Create
        await axios.post(
          `${API}/directory/admin/categories?admin_id=${user.id}&name_fr=${encodeURIComponent(formData.name_fr)}&name_en=${encodeURIComponent(formData.name_en)}`
        );
        toast.success('Catégorie créée');
      }

      fetchCategories();
      handleClose();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (category) => {
    setEditCategory(category);
    setFormData({
      name_fr: category.name_fr,
      name_en: category.name_en
    });
    setDialogOpen(true);
  };

  const handleDelete = async (categoryId) => {
    if (!confirm('Supprimer cette catégorie ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/directory/admin/categories/${categoryId}?admin_id=${user.id}`);
      toast.success('Catégorie supprimée');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditCategory(null);
    setFormData({ name_fr: '', name_en: '' });
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0A2540] mb-2">
              Catégories de l'Annuaire
            </h1>
            <p className="text-gray-600">
              Gérez les catégories professionnelles de l'annuaire
            </p>
          </div>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-[#635BFF] hover:bg-[#5248d9]"
          >
            <FaPlus className="mr-2" />
            Nouvelle Catégorie
          </Button>
        </div>

        {/* Categories List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <Card key={category.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-[#0A2540]">
                      {category.name_fr}
                    </h3>
                    <p className="text-sm text-gray-600">{category.name_en}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(category)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(category.id)}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  ID: {category.id}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {categories.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              Aucune catégorie
            </CardContent>
          </Card>
        )}

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={handleClose}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name_fr">Nom (Français) *</Label>
                <Input
                  id="name_fr"
                  required
                  value={formData.name_fr}
                  onChange={(e) => setFormData({...formData, name_fr: e.target.value})}
                  placeholder="Ex: Bureaux d'études"
                />
              </div>

              <div>
                <Label htmlFor="name_en">Nom (English) *</Label>
                <Input
                  id="name_en"
                  required
                  value={formData.name_en}
                  onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                  placeholder="Ex: Engineering Firms"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#635BFF] hover:bg-[#5248d9]">
                  {editCategory ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
